import React, { useEffect, useRef } from 'react';
import { Spin, Button } from 'antd';
import { LoginOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component
 * Ensures user is authenticated before accessing protected content
 * Shows loading spinner while checking authentication
 * Redirects to login if not authenticated
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const loginCalledRef = useRef(false);

  useEffect(() => {
    // Only trigger login once if not authenticated and not already in progress
    if (!isLoading && !isAuthenticated && !loginCalledRef.current) {
      loginCalledRef.current = true;
      login();
    }
  }, [isAuthenticated, isLoading, login]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Spin size="large" />
        <p>Verificando autenticación...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Show a login prompt instead of auto-redirecting
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <h2>Autenticación requerida</h2>
        <p>Debe iniciar sesión para acceder a esta aplicación</p>
        <Button
          type="primary"
          icon={<LoginOutlined />}
          size="large"
          onClick={() => {
            if (!loginCalledRef.current) {
              loginCalledRef.current = true;
              login();
            }
          }}
          style={{
            backgroundColor: '#025940',
            borderColor: '#025940',
          }}
        >
          Iniciar sesión con Microsoft
        </Button>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
